import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, tap, switchMap } from "rxjs";
import { environment } from "../../../environments/environment";
import { LoginRequest, LoginResponse, AuthUser } from "../models/auth.models";

@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly tokenKey = "vial_token";
  private readonly refreshKey = "vial_refresh";
  private readonly userKey = "vial_user";

  private readonly _user$ = new BehaviorSubject<AuthUser | null>(
    this.readUser()
  );
  readonly user$ = this._user$.asObservable();

  private readonly baseUrl = environment.baseUrl;

  constructor(private http: HttpClient) {}

  // ─── Helpers privados ──────────────────────────────

  private toAuthUser(raw: AuthUser): AuthUser {
    return {
      ...raw,
      name: `${raw.firstName ?? ""} ${raw.lastName ?? ""}`.trim(),
    };
  }

  private readUser(): AuthUser | null {
    const raw = localStorage.getItem(this.userKey);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      localStorage.removeItem(this.userKey);
      return null;
    }
  }

  // ─── Auth ──────────────────────────────────────────

  /** GET /auth/me */
  me(): Observable<AuthUser> {
    return this.http.get<AuthUser>(`${this.baseUrl}/auth/me`).pipe(
      tap((raw) => {
        const user = this.toAuthUser(raw);
        localStorage.setItem(this.userKey, JSON.stringify(user));
        this._user$.next(user);
      })
    );
  }

  /** POST /auth/login → guarda accessToken + refreshToken, luego carga /auth/me */
  login(payload: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.baseUrl}/auth/login`, payload)
      .pipe(
        tap((res) => {
          localStorage.setItem(this.tokenKey, res.accessToken);
          localStorage.setItem(this.refreshKey, res.refreshToken);
        }),
        switchMap((res) =>
          this.me().pipe(
            switchMap((meUser) => {
              const merged: LoginResponse = { ...res, user: meUser, me: meUser };
              return [merged];
            })
          )
        )
      );
  }

  /** POST /auth/refresh → rota ambos tokens */
  refreshTokens(): Observable<{ accessToken: string; refreshToken: string }> {
    const refreshToken = localStorage.getItem(this.refreshKey);
    return this.http
      .post<{ accessToken: string; refreshToken: string }>(
        `${this.baseUrl}/auth/refresh`,
        { refreshToken }
      )
      .pipe(
        tap((tokens) => {
          localStorage.setItem(this.tokenKey, tokens.accessToken);
          localStorage.setItem(this.refreshKey, tokens.refreshToken);
        })
      );
  }

  /** POST /auth/logout → invalida refreshToken en el servidor */
  logoutRemote(): void {
    this.http.post(`${this.baseUrl}/auth/logout`, {}).subscribe();
  }

  logout(): void {
    this.logoutRemote();
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshKey);
    localStorage.removeItem(this.userKey);
    this._user$.next(null);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshKey);
  }

  isLoggedIn(): boolean {
    return Boolean(this.getToken());
  }

  currentUser(): AuthUser | null {
    return this._user$.value;
  }
}
